/**
 * Pure enum constants and types for the chat / workspace / artifact schema.
 *
 * These are wire values: the DB column types, the tRPC validators, the AI
 * message-metadata schema, and the SDK's surface predicates all derive from
 * this one list. It stays free of Node-flavored imports (`nanoid`,
 * `drizzle-orm/pg-core`, table definitions) because it is reachable from both
 * the `"use workflow"` function bundle and the browser SDK — and, for the
 * artifact values, from `apps/artifacts-host`, which may import no
 * `@workspace/*` package at all.
 *
 * `@workspace/database/schema/chat/values` re-exports everything here, and
 * `schema.ts` re-exports that, so existing
 * `@workspace/database/schema/chat` callers keep working unchanged. The
 * artifact schema re-exports its own two in the same way.
 */

export const feedbackStatusValues = [
  "submitted",
  "reviewing",
  "closed",
] as const;
export const feedbackCategoryValues = [
  "not_categorized",
  "interface",
  "missing_knowledge",
  "hallucination",
  "tool_call_issue",
  "win_knowledge",
  "win_tool",
  "win_general",
] as const;
export const feedbackVoteValues = ["up", "down"] as const;
/**
 * What a feedback row is *about*. `message` rows come from the thumbs
 * up/down control on a single assistant message and carry a chat, a vote,
 * and a conversation-context blob. `app` rows come from the app-wide
 * "Share feedback" dialog: free-text about the product as a whole, with a
 * `pagePath` instead of a chat. `insights_attention` rows come from the
 * thumbs on one card of the Insights "What needs my attention" panel
 * and carry a vote plus an `attentionCardId` (ADR-118). All three live in
 * `user_feedback` so admins triage one queue with one set of statuses,
 * categories, and notes.
 */
export const feedbackTypeValues = [
  "message",
  "app",
  "insights_attention",
] as const;
export const appSurfaceValues = [
  "standalone-web",
  "standalone-mobile",
  "excel-add-in",
  "outlook-add-in",
  "word-add-in",
  // Generic surface for third-party / embedded apps that consume Magnus via
  // the SDK (`@bg-bgi/magnus-sdk`). Structured details about the host travel
  // separately in the request `surfaceContext`. Stored as free text
  // (the column is `text`, not a pg enum), so no migration is required.
  "embedded",
  // Procore side-panel copilot: Magnus runs inside a cross-site iframe hosted
  // by Procore. Not an Office host, so it is intentionally excluded from
  // `officeAddinSurfaces`; its host context arrives over postMessage.
  "procore-copilot",
  "freshservice-copilot",
  // IT's AppCopilotPoc: a .NET host + React SPA behind a Duende IdentityServer
  // BFF. Authenticates via the trusted OIDC issuer registry (ADR-083), not the
  // Entra exchange, so it is intentionally excluded from `embeddedSurfaces`
  // (no Magnus-owned shell) and `officeAddinSurfaces`.
  "appcopilot",
  // Magnus Create (`/create`): design-mode surface where the model iterates on
  // an HTML artifact with a live preview. A standalone-web host, but a distinct
  // surface so capability gating and chat scoping can key off it.
  "create",
] as const;
export const chatVisibilityValues = ["private", "shared"] as const;

/**
 * Who an artifact's own address serves, before any share link is considered:
 *   - private   — only the owner, the workspace, and explicit hand-outs.
 *   - public    — plus anyone signed in to the company (whole-company).
 *   - anonymous — plus the whole internet, no key and no sign-in. Reserved for
 *                 externally marketed sites, so it is settable only by a
 *                 platform admin (`admin-artifacts:manage`) and, once set,
 *                 locks the artifact's visibility against its own author.
 *
 * `?key=` share links are orthogonal to all three: they grant anonymous access
 * to an otherwise private artifact and are unaffected by this column.
 *
 * Owned here rather than in the schema because `apps/artifacts-host` decides
 * whether to serve a page from this value and cannot import `@workspace/*`
 * infra. `@workspace/database/schema/artifact` re-exports it, so nothing about
 * the DB-side import sites changes. See ADR-112.
 */
export const artifactVisibilityValues = [
  "private",
  "public",
  "anonymous",
] as const;
export type ArtifactVisibility = (typeof artifactVisibilityValues)[number];

/**
 * How a viewer reached an artifact site:
 *   - member      — signed-in Magnus user (owner, share target, or whole-company).
 *   - share-link  — anonymous visitor holding a `?key=` share link.
 *   - anonymous   — anonymous visitor on a `visibility="anonymous"` artifact,
 *                   holding no key at all. Kept separate from `share-link`
 *                   because the two answer different questions: who did we hand
 *                   a link to, versus who found the public site.
 *   - preview     — an editor previewing a specific (often unpublished)
 *                   revision. Recorded so the history is complete, but excluded
 *                   from every headline figure.
 *
 * Lives beside {@link artifactVisibilityValues} because the host derives it
 * from the same serve decision.
 */
export const artifactViewSourceValues = [
  "member",
  "share-link",
  "anonymous",
  "preview",
] as const;
export type ArtifactViewSource = (typeof artifactViewSourceValues)[number];

/**
 * Why the background summary job permanently skipped a chat. Today the
 * only reason is `content_filtered` — the transcript trips Azure
 * OpenAI's content management policy, so re-running the identical input
 * is futile (see ADR-071 / the chat-summary workflow). Recorded as a
 * tombstone on `chat` so the hourly selector stops re-dispatching it;
 * the chat becomes eligible again only if it's edited after the skip.
 */
export const summarySkippedReasonValues = ["content_filtered"] as const;

export type FeedbackStatus = (typeof feedbackStatusValues)[number];
export type FeedbackCategory = (typeof feedbackCategoryValues)[number];
export type FeedbackVote = (typeof feedbackVoteValues)[number];
export type FeedbackType = (typeof feedbackTypeValues)[number];
export type AppSurface = (typeof appSurfaceValues)[number];
export type ChatVisibility = (typeof chatVisibilityValues)[number];
export type SummarySkippedReason = (typeof summarySkippedReasonValues)[number];

/**
 * Surfaces that run inside a Microsoft Office host (Excel today;
 * Word/PowerPoint/Outlook later). Maintained as an explicit subset of
 * `appSurfaceValues` rather than inferred from the surface name, because
 * suffix-matching on `*-add-in` is brittle.
 *
 * Adding a new Office host is a paired one-line addition: append to
 * `appSurfaceValues` AND `officeAddinSurfaces`.
 */
export const officeAddinSurfaces = [
  "excel-add-in",
  "outlook-add-in",
  "word-add-in",
] as const satisfies readonly AppSurface[];
export type OfficeAddinSurface = (typeof officeAddinSurfaces)[number];

export function isOfficeAddin(s: AppSurface): s is OfficeAddinSurface {
  return (officeAddinSurfaces as readonly string[]).includes(s);
}

/**
 * Surfaces that run as an **embedded copilot** — a host shell mounted inside
 * a third-party container (the Office add-ins inside their WebView, the
 * Procore panel inside its cross-site iframe). Superset of
 * `officeAddinSurfaces`. This is the taxonomy the embedded-host registry,
 * session stores, and proxy gate key off of. The generic `"embedded"` SDK
 * surface is intentionally NOT here — it has no Magnus-owned shell or session.
 *
 * Adding an embedded copilot is a paired one-line addition: append to
 * `appSurfaceValues` AND `embeddedSurfaces` (+ a registry entry + a shell).
 *
 * This list lives here (not derived from the `@workspace/copilots` registry)
 * because the DB-schema layer must stay reachable from edge/runtime bundles
 * and cannot import that higher layer. `embedded-hosts.test.ts` asserts this
 * set is exactly the union of the copilot descriptors' `surfaces`, so the two
 * can't drift.
 */
export const embeddedSurfaces = [
  ...officeAddinSurfaces,
  "procore-copilot",
  "freshservice-copilot",
] as const satisfies readonly AppSurface[];
export type EmbeddedSurface = (typeof embeddedSurfaces)[number];

export function isEmbeddedSurface(s: AppSurface): s is EmbeddedSurface {
  return (embeddedSurfaces as readonly string[]).includes(s);
}

/**
 * Single source of truth for human-readable copilot-surface labels.
 * Every subscriber (chat UI, admin metrics/analytics/feedback dashboards,
 * filters) pulls from here so naming stays consistent in one place.
 *
 * `satisfies Record<AppSurface, string>` makes this exhaustive: a new
 * `AppSurface` won't typecheck until it has a label here. `surfaceLabel()`
 * still falls through to the raw slug at runtime for any value not mapped
 * (e.g. legacy/unknown rows), so unexpected surfaces render legibly.
 */
export const SURFACE_LABELS = {
  "standalone-web": "Web",
  "standalone-mobile": "Mobile",
  "excel-add-in": "Excel",
  "outlook-add-in": "Outlook",
  "word-add-in": "Word",
  embedded: "Embedded",
  "procore-copilot": "Procore",
  "freshservice-copilot": "Freshservice",
  appcopilot: "AppCopilot",
  create: "Create",
} satisfies Record<AppSurface, string>;

export function surfaceLabel(surface: string): string {
  return (SURFACE_LABELS as Record<string, string>)[surface] ?? surface;
}

/**
 * Single source of truth for the column-length caps on `workspace.name`
 * and `workspace.description`. Consumed by the tRPC validator, the prompt
 * module, and the workspace UI.
 */
export const WORKSPACE_NAME_MAX = 100;
export const WORKSPACE_DESCRIPTION_MAX = 1000;
export const WORKSPACE_INSTRUCTIONS_MAX = 10_000;

/**
 * Cap on a creation's display name (`create_project.name`). The column is
 * untyped text, so this is the only limit there is — shared by the rename
 * validator and the dialog's `maxLength` so the client counter and the server
 * rejection agree.
 */
export const CREATE_PROJECT_NAME_MAX = 100;

/**
 * `name` + `suffix` within {@link CREATE_PROJECT_NAME_MAX}: a name already at
 * the cap loses characters to the suffix rather than the whole request to a
 * validation error.
 */
export function suffixedCreateProjectName(
  name: string,
  suffix: string
): string {
  const head = name.trim();
  const room = CREATE_PROJECT_NAME_MAX - suffix.length;
  return `${head.length <= room ? head : head.slice(0, room).trimEnd()}${suffix}`;
}

/**
 * Practical cap on a user's global custom instructions (`user.custom_instructions`).
 * These are injected into the system prompt of every model call for that user,
 * so the limit is deliberately tighter than workspace instructions to bound the
 * recurring token cost. Consumed by the tRPC validator, the prompt module, and
 * the settings UI.
 */
export const USER_CUSTOM_INSTRUCTIONS_MAX = 4000;

/**
 * Wire cap on the client-passed `surfacePromptAppendix` turn option
 * (plan 032 §B2). Enforced by the chat route's zod schema and re-applied
 * as defense-in-depth truncation in the prompt builder. Untrusted host
 * input rendered into every turn's system prompt, so the limit is tight —
 * matching the user custom-instructions budget.
 */
export const SURFACE_PROMPT_APPENDIX_MAX = 4000;

/**
 * Separator between the surface namespace and a host-declared tool's plain
 * name in the model-visible wire name (`workforce__navigate_in_app`). The
 * host author only ever writes the plain name; the server prepends
 * `{appSurface}__` when it builds the model's tool map. No server-declared
 * tool name contains `__` — pinned by a unit test over the static tool
 * registry — so collision with a server tool is structurally impossible.
 */
export const HOST_TOOL_NAMESPACE_SEPARATOR = "__";

/**
 * Plain (developer-written) host-declared tool name: snake_case, 1..48
 * chars. Single underscores only — the shape structurally cannot contain
 * the `__` namespace separator, so the namespaced wire name always parses
 * back unambiguously.
 */
export const HOST_TOOL_NAME_RE =
  /^(?=[a-z0-9_]{1,48}$)[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

export const DEFAULT_CHAT_TITLE = "New chat";

export const VOICE_CHAT_TITLE = "Voice chat";

/**
 * Wire copy of the upload processing-status enum. Authored here rather than
 * imported from `@workspace/database/schema/upload` because the contract
 * must stay free of server imports (see the package rule in `index.ts`).
 * A compile-time assertion in `@workspace/ai`
 * (`src/__tests__/contract-drift-check.ts`) keeps this copy and the DB enum
 * in lockstep — adding a value to either side alone fails `check-types`.
 */
export const processingStatusValues = [
  "awaiting-source",
  "deferred",
  "pending",
  "processing",
  "extracting-text",
  "text-ready",
  "chunks-ready",
  "completed",
  "failed",
] as const;
export type ProcessingStatus = (typeof processingStatusValues)[number];

/**
 * Wire copy of the selectable model ids. Authored here rather than imported
 * from `@workspace/ai` because the contract must stay free of server imports
 * (see the package rule in `index.ts`) — the server's `modelConfigs` carries
 * cost multipliers and gateway ids that must not leak into a consumer's
 * declaration tree. A compile-time assertion in `@workspace/ai`
 * (`src/__tests__/contract-drift-check.ts`) keeps this copy and the server's
 * `ModelId` in lockstep — adding a value to either side alone fails
 * `check-types`.
 */
export const modelIds = [
  "gpt-6.1-sol",
  "gpt-6-luna",
  "gpt-6-astra",
  "claude-sonnet-5.5",
  "claude-sonnet-4.6",
  "claude-opus-5.5",
  "claude-haiku-5.5",
  "claude-fable-5.1",
  "gemini-4-argon",
  "gemini-3.8-flash",
  "grok-4.7",
  "muse-spark-1.3",
  "muse-wonder-1.0",
] as const;
export type ModelId = (typeof modelIds)[number];

/**
 * Wire copy of the selectable model id or `"auto"` ("Auto" in the picker).
 * Auto runs the Jev routing step while `models.auto-route.enable` is on
 * (never on Create) and is otherwise the surface default; either way it is
 * resolved to a concrete `ModelId` inside the chat workflow. Mirrors
 * `ModelSelection` in `@workspace/ai` (`models/routing.ts`); the same
 * drift-check assertion applies.
 *
 * `"smart-auto"` is deprecated: Smart Auto merged into Auto, and the server
 * treats it exactly as `"auto"`. It stays accepted for one release so
 * remembered picker preferences and older SDK callers keep working; send
 * `"auto"` instead.
 */
export const modelSelections = ["auto", "smart-auto", ...modelIds] as const;
export type ModelSelection = (typeof modelSelections)[number];

/**
 * Wire copy of the per-turn thinking-depth levels, authored here for the same
 * reason as {@link modelIds} — the server's `reasoningLevelSchema` lives in
 * `@workspace/ai`, which the contract must not import. Pinned to the server's
 * `ReasoningLevel` by the same compile-time assertion in
 * `@workspace/ai` (`src/__tests__/contract-drift-check.ts`).
 *
 * Note this is the *outbound* copy, used by `TurnOptions` so a caller can only
 * request a level the server accepts. The inbound `messageMetadataSchema.reasoningLevel`
 * stays a plain string on purpose — a client should not fail to parse a
 * transcript from a server that has since added a level.
 */
export const reasoningLevels = ["low", "medium", "high"] as const;
export type ReasoningLevel = (typeof reasoningLevels)[number];

/**
 * Wire copies of Auto routing's per-turn move and escalate reason (ADR-127),
 * for `messageMetadataSchema`. The server's `RoutingMove` and
 * `EscalateReason` live in `@workspace/ai`; pinned to them by the same
 * compile-time assertion as {@link reasoningLevels}.
 */
export const routeMoves = ["escalate", "forced", "none"] as const;
export type RouteMove = (typeof routeMoves)[number];
export const escalateReasons = ["tool-error"] as const;
export type EscalateReason = (typeof escalateReasons)[number];
/**
 * Insights usage-telemetry event names (ADR-117).
 *
 * Authored here rather than in the analytics registry because two tiers need
 * them and only `@magnus/contract` is importable by both: the registry itself
 * lives in `@workspace/services`, while the emitting funnels live in
 * `@workspace/insights`, which may not import services (see
 * `scripts/check-boundaries.mjs`). `ANALYTICS_EVENT_NAMES` spreads this tuple
 * in, so these stay part of the one closed union clients infer.
 *
 * Names follow the registry's rules: `<area>_<noun>_<verb-past-tense>`,
 * lowercase snake_case, LOW-CARDINALITY. Drawer mode, insight id, metric path,
 * outcome and scope all ride in `properties`, never in the name.
 */
export const insightsClientUsageEventNames = [
  /** The dashboard was opened — one per visit, the denominator for depth. */
  "insights_dashboard_opened",
  /** An org node was chosen in the scope picker (expanding a node is not this). */
  "insights_scope_selected",
  /** A detail or trend drawer was opened; `mode` distinguishes them. */
  "insights_drawer_opened",
  /** A link out to a Power BI report was clicked. */
  "insights_report_clicked",
  // ── Attention panel (ADR-118). APPEND ONLY below this line: the read
  // service destructures this tuple positionally, so inserting mid-list
  // silently re-labels every KQL query downstream. ──
  /**
   * The "What needs my attention" panel was expanded — the panel is
   * collapsed by default, so mount is not "shown". `itemIds` carries the
   * `|`-joined ids of every card rendered, which is what "shown by item"
   * reads back.
   */
  "insights_attention_panel_opened",
  /** A card was clicked through to its tile drawer. */
  "insights_attention_card_clicked",
  /** The AI-derived evidence footer on a card was expanded. */
  "insights_attention_evidence_expanded",
  /**
   * A thumbs vote landed on a card. Emitted by the server from the feedback
   * write path (so it cannot be posted without a stored vote), but listed
   * here rather than in the server tuple so the four attention names read
   * back as one positional block.
   */
  "insights_attention_feedback_submitted",
  // ── All-employees audience. APPEND ONLY, same rule as above. A separate
  // name, not a property on `insights_dashboard_opened`, so the executive
  // KPIs (which filter by name) can never count a company-audience visit and a
  // client cannot mislabel its way into or out of them. ──
  /** The all-employees dashboard was opened — one per visit. */
  "insights_company_dashboard_opened",
] as const;

/**
 * Emitted only by the server, from the `insights.metric` procedure.
 *
 * Kept out of `ANALYTICS_EVENT_NAMES` deliberately: that tuple is the zod enum
 * `analytics.track` accepts from clients, so a name listed there is
 * client-writable. A caller who could post this event would be writing
 * directly into the admin tab's per-insight counts, error-category breakdown
 * and latency figures. The cache warmer never produces it either — it calls
 * the data service rather than the router.
 */
export const insightsServerUsageEventNames = ["insights_metric_shown"] as const;

export const insightsUsageEventNames = [
  ...insightsClientUsageEventNames,
  ...insightsServerUsageEventNames,
] as const;

export type InsightsClientUsageEventName =
  (typeof insightsClientUsageEventNames)[number];
export type InsightsUsageEventName = (typeof insightsUsageEventNames)[number];

/** Outcome of an `insights_metric_shown` execution. */
export const insightsUsageOutcomes = ["success", "partial", "error"] as const;
export type InsightsUsageOutcome = (typeof insightsUsageOutcomes)[number];

/** Drawer kind for `insights_drawer_opened`. */
export const insightsDrawerModes = ["detail", "trend"] as const;
export type InsightsDrawerMode = (typeof insightsDrawerModes)[number];

/**
 * Which tiers produced a persisted attention panel (ADR-118).
 *
 * - `rule-only` — deterministic rules only; the LLM derive pass was off or
 *   skipped (no items to rank, no model configured).
 * - `derived` — the derive pass ran and its result was merged.
 * - `degraded` — the derive pass was attempted and failed; the panel shipped
 *   rule items only and was NOT cached. `degradedReason` says why.
 */
export const attentionPanelTiers = [
  "rule-only",
  "derived",
  "degraded",
] as const;
export type AttentionPanelTier = (typeof attentionPanelTiers)[number];

/**
 * Why a model-proposed derived item was dropped before it reached the panel,
 * one per grounding gate in `buildDerivedItems`. Persisted on the panel row so
 * derived-tier health can be read without replaying the model call.
 */
export const attentionDropReasons = [
  /** A citation named a query id the model never executed. */
  "unusable-citation",
  /** A citation named an element the cited query did not return. */
  "unknown-element",
  /** A citation's row indices fell outside the rows the model was shown. */
  "rows-out-of-range",
  /** The item pointed at a tile id that is not in the catalog. */
  "unknown-tile",
  /** A numeric token in the copy matched no cited cell or expression. */
  "ungrounded-figures",
  /** The model reused an id already taken by a rule or earlier derived item. */
  "duplicate-id",
] as const;
export type AttentionDropReason = (typeof attentionDropReasons)[number];

/**
 * Separator for the canonical scope dimension on Insights usage events.
 *
 * The format (`region|subregion|division`) is the wire contract between the
 * emitting funnel in `@workspace/insights` and the admin tab in `apps/web`,
 * which may not import that package — so the encode/decode pair lives here,
 * where both tiers already import the event names.
 */
const INSIGHTS_SCOPE_SEPARATOR = "|";

/** The three org levels a scope selection carries, in canonical order. */
export const insightsScopeLevels = ["region", "subregion", "division"] as const;
export type InsightsScopeLevel = (typeof insightsScopeLevels)[number];

/**
 * Canonical scope string. Company-wide encodes as `"||"` — a real, countable
 * value rather than an absent one, so a deliberate Company selection stays
 * distinguishable from no selection at all.
 */
export function encodeInsightsScope(
  scope: Partial<Record<InsightsScopeLevel, string | undefined>>
): string {
  return insightsScopeLevels
    .map((level) => scope[level]?.trim() ?? "")
    .join(INSIGHTS_SCOPE_SEPARATOR);
}

/** Human label for a canonical scope string, for display surfaces. */
export function formatInsightsScope(
  scope: string,
  companyLabel = "Company-wide"
): string {
  const parts = scope
    .split(INSIGHTS_SCOPE_SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length === 0 ? companyLabel : parts.join(" \u203a ");
}

/**
 * Scope as event dimensions: the canonical string (what "distinct scopes
 * selected" counts) plus each level that is actually set.
 *
 * Unset levels are omitted rather than sent as `""`, so an absent subregion
 * and a subregion literally named empty never share a bucket — the emit-time
 * analogue of `NULLIF(btrim(...), '')`.
 *
 * Lives here so the client funnel and the server-side execution emit build
 * identical dimensions from one rule; a fourth level or a changed trim rule
 * cannot desync them.
 */
export function insightsScopeDimensions(
  scope: Partial<Record<InsightsScopeLevel, string | undefined>>
): Record<string, string> {
  const dimensions: Record<string, string> = {
    [INSIGHTS_USAGE_PROPERTIES.scope]: encodeInsightsScope(scope),
  };
  for (const level of insightsScopeLevels) {
    const value = scope[level]?.trim();
    if (value) {
      dimensions[level] = value;
    }
  }
  return dimensions;
}

/**
 * Property (custom-dimension) names on Insights usage events.
 *
 * Shared between the emitters and the KQL that reads them back, because a
 * mismatch between the two is the worst failure mode this pipeline has: the
 * query returns no rows rather than an error, so the admin tab shows a
 * confident zero instead of "something is wrong". Importing one constant on
 * both sides makes a rename a type error rather than a silent outage.
 */
export const INSIGHTS_USAGE_PROPERTIES = {
  userId: "userId",
  department: "department",
  jobTitle: "jobTitle",
  insightId: "insightId",
  path: "path",
  mode: "mode",
  outcome: "outcome",
  errorCategory: "errorCategory",
  cacheDisposition: "cacheDisposition",
  durationMs: "durationMs",
  target: "target",
  scope: "scope",
  region: "region",
  subregion: "subregion",
  division: "division",
  // Attention panel (ADR-118). `itemIds` is the `|`-joined list on
  // `insights_attention_panel_opened`; every other event carries one card.
  panelId: "panelId",
  cardId: "cardId",
  itemId: "itemId",
  itemIds: "itemIds",
  provenance: "provenance",
  severity: "severity",
  tileId: "tileId",
  tier: "tier",
  cardCount: "cardCount",
  derivedCount: "derivedCount",
  vote: "vote",
} as const;

export type InsightsUsagePropertyName =
  (typeof INSIGHTS_USAGE_PROPERTIES)[keyof typeof INSIGHTS_USAGE_PROPERTIES];

/**
 * Server-owned dimensions a caller may never set.
 *
 * `analytics.track` accepts an open `properties` record, so without this the
 * spread order alone decides whether a client can attribute an event to
 * another user. Stripped and re-applied server-side by `emitTrackedEvent`.
 */
export const RESERVED_EVENT_PROPERTIES: readonly string[] = [
  INSIGHTS_USAGE_PROPERTIES.userId,
  INSIGHTS_USAGE_PROPERTIES.department,
  INSIGHTS_USAGE_PROPERTIES.jobTitle,
  // The Azure Monitor marker that decides which table the record lands in.
  "microsoft.custom_event.name",
];
